import React from 'react';
import {
    ButtonGroup,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Flex,
    VStack,
    Box,
    Button,
    IconButton,
} from '@cad-arc';
import { AttributeTypeEnumType, FormResource, PatrolForm, PatrolResource } from '@mark43/cad-api';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { useReleaseUnitsMutation } from '../../unit-context-menu/use-release-units-mutation';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormCheckbox } from '../../forms/integrated-form-controls/form-checkbox';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../../forms/integrated-form-controls/form-location-select';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { PatrolRequest } from '../types';
import { PatrolStartTimeDisplay } from './patrol-start-time-display';
import { useLocationApi } from '../../location-select/hooks/use-location-api';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useApplicationType } from '../../common/hooks/use-application-type';

function EndPatrolButton(props: {
    unitId: number;
    closeDrawer: (...args: any[]) => any;
    releaseUnitsMutation: ReturnType<typeof useReleaseUnitsMutation>;
}): React.ReactElement {
    const integratedForm = useIntegratedForm<PatrolForm>();
    return (
        <Button
            {...integratedForm.getSaveButtonProps()}
            variant="outline"
            onClick={() => {
                props.releaseUnitsMutation.mutate([props.unitId], {
                    onSuccess() {
                        props.closeDrawer();
                    },
                });
            }}
        >
            End Patrol
        </Button>
    );
}

function AddNewPatrolButton(props: { onClick: (...args: any[]) => any }): React.ReactElement {
    const integratedForm = useIntegratedForm<PatrolForm>();
    return (
        <IconButton
            variant="outline"
            size="md"
            aria-label="create"
            icon="Add"
            onClick={props.onClick}
            {...integratedForm.getGenericButtonProps()}
        />
    );
}

export const PatrolDrawerContent: React.FC<{
    header: React.ReactNode;
    attributeType: AttributeTypeEnumType;
    unitId?: number;
    statusId?: number;
    closeDrawer: () => void;
}> = ({ header, attributeType, unitId, statusId, closeDrawer }) => {
    const apiClient = useApiClient();
    const isAdminStatusForm = attributeType === 'ADMIN_PATROL';
    const [mode, setMode] = useState<'create' | 'edit'>('create');
    const { payForEsriLocation } = useLocationApi();
    const { subpremiseSupport, firstResponderCanEditUnitLocations } = useEnabledFeatures();
    const releaseUnitsMutation = useReleaseUnitsMutation();
    const { isFirstResponder } = useApplicationType();

    const allowedToUpdateUnitLocation = !isFirstResponder || firstResponderCanEditUnitLocations;

    const query = useQuery(['directed-patrol', unitId], () => {
        if (unitId) {
            return apiClient
                .request<PatrolResource.BootstrapPatrol>({
                    method: 'POST',
                    url: 'patrol/bootstrap/',
                    data: { unitId, patrolTypeAttrType: attributeType },
                })
                .then((resp) => resp.data.data);
        }
    });

    const { data: patrolBootstrapResponse } = query;

    const mutation = useMutation(
        ({
            patrolView,
            updateUnitLocation,
        }: {
            patrolView: PatrolRequest;
            updateUnitLocation: boolean;
        }) => {
            return apiClient.request<FormResource.UpsertPatrol>({
                method: 'POST',
                url: 'form/patrol/',
                params: {
                    update_unit_location: updateUnitLocation,
                },
                // @ts-expect-error not including versioning fields
                data: patrolView,
            });
        },
        {
            onSuccess() {
                closeDrawer();
            },
        }
    );

    const form = useForm<PatrolForm>({
        mode: 'onBlur',
    });
    const { reset, setValue } = form;

    useEffect(() => {
        if (patrolBootstrapResponse) {
            reset(patrolBootstrapResponse.form, {
                keepDefaultValues: true,
            });
            setMode(patrolBootstrapResponse.form.createdDateUtc ? 'edit' : 'create');
        }
    }, [reset, patrolBootstrapResponse]);

    const handleAddNewPatrol = useCallback(() => {
        setMode('create');
        setValue('consolidatedLocationView', undefined);
        setValue('subPremise', '');
        setValue('comments', '');
    }, [setMode, setValue]);

    const combinedMutation = React.useMemo(() => {
        return {
            isLoading: mutation.isLoading || releaseUnitsMutation.isLoading,
            isError: mutation.isError || releaseUnitsMutation.isError,
            mutate: mutation.mutate,
            mutateAsync: mutation.mutateAsync,
        };
    }, [mutation, releaseUnitsMutation]);

    if (!unitId || !statusId) {
        return (
            <DrawerContent>
                <DrawerHeader>{header}</DrawerHeader>
            </DrawerContent>
        );
    }

    function onSubmit(data: PatrolForm): void {
        if (!data.patrolType || !unitId || !statusId) {
            return;
        }
        if (form.getFieldState('consolidatedLocationView').isDirty) {
            const location = form.getValues('consolidatedLocationView');
            if (location !== undefined) {
                payForEsriLocation(location);
            }
        }
        return mutation.mutate({
            patrolView: {
                // @ts-expect-error id not on patrolrequest but sometimes we send one anyways
                id: mode === 'edit' ? patrolBootstrapResponse?.form.id : undefined,
                unitIds: [unitId],
                unitId,
                comments: data.comments,
                location: data.consolidatedLocationView,
                patrolTypeAttrId: data.patrolType.value,
                patrolTypeAttrType: attributeType,
                unitStateId: statusId,
            },
            updateUnitLocation: allowedToUpdateUnitLocation ? data.updateUnitLocation : false,
        });
    }

    return (
        <IntegratedFormProvider
            query={query}
            mutation={combinedMutation}
            form={form}
            onSubmit={onSubmit}
        >
            <DrawerContent>
                <DrawerHeader>
                    <Flex width="93%" justifyContent="space-between">
                        {header}
                        {mode === 'edit' && <AddNewPatrolButton onClick={handleAddNewPatrol} />}
                    </Flex>
                </DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <VStack spacing={4} align="left">
                            {mode === 'edit' && patrolBootstrapResponse?.form.createdDateUtc ? (
                                <PatrolStartTimeDisplay
                                    createdDateUtc={patrolBootstrapResponse?.form.createdDateUtc}
                                />
                            ) : undefined}
                            {isAdminStatusForm ? undefined : (
                                <FormSelect
                                    autoFocus={true}
                                    name="patrolType"
                                    label="Patrol Type"
                                    rules={{ required: 'Required' }}
                                    control={form.control}
                                    isDisabled={mode === 'edit'}
                                    options={
                                        patrolBootstrapResponse?.options.patrolTypeOptions ?? []
                                    }
                                />
                            )}
                            <FormLocationSelect
                                autoFocus={isAdminStatusForm}
                                name="consolidatedLocationView"
                                rules={{ required: 'Required' }}
                                control={form.control}
                                label="Location"
                                isDisabled={mode === 'edit'}
                            />
                            {!subpremiseSupport && (
                                <Flex direction="row" mt={2}>
                                    <Box mr={2}>
                                        <FormInput
                                            name="subPremise"
                                            control={form.control}
                                            isDisabled={mode === 'edit'}
                                            placeholder="Apt, Unit"
                                            inputProps={{
                                                maxLength: 200,
                                            }}
                                        />
                                    </Box>
                                </Flex>
                            )}
                            {mode === 'create' && allowedToUpdateUnitLocation && (
                                <FormCheckbox
                                    name="updateUnitLocation"
                                    control={form.control}
                                    aria-label="Update Unit Location"
                                    label="Update Unit Location"
                                />
                            )}
                            <FormTextarea label="Comments" name="comments" control={form.control} />
                        </VStack>
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    {mode === 'edit' ? (
                        <Flex w="100%" justifyContent="space-between">
                            <FormCancelButton
                                onClick={() => {
                                    closeDrawer();
                                }}
                            />
                            <ButtonGroup>
                                <EndPatrolButton
                                    unitId={unitId}
                                    releaseUnitsMutation={releaseUnitsMutation}
                                    closeDrawer={closeDrawer}
                                />
                                <FormSaveButton />
                            </ButtonGroup>
                        </Flex>
                    ) : (
                        <ButtonGroup>
                            <FormCancelButton
                                onClick={() => {
                                    closeDrawer();
                                }}
                            />
                            <FormSaveButton />
                        </ButtonGroup>
                    )}
                </DrawerFooter>
            </DrawerContent>
        </IntegratedFormProvider>
    );
};
